import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid2,
  CircularProgress,
  TextField,
  InputAdornment,
  Stack,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import config from "../common/Config";
import "../common/commonStyles.css";
import { buildDatatablePayload, postDashboardDatatable } from "../../utils/dashboardApi";

const DeliveredInvoice = () => {
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const s = localStorage.getItem('startDate');
  const e = localStorage.getItem('endDate');

  const formatDate = (date) => {
    if (!date) return "0";
    const [year, month, day] = date.split("-");
    return `${day}-${month}-${year}`;
  };

  const getCurrentMonthDates = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    const startOfMonth = new Date(year, month, 1);
    const startDay = String(startOfMonth.getDate()).padStart(2, "0");
    const startMonth = String(startOfMonth.getMonth() + 1).padStart(2, "0");

    const endOfMonth = new Date(year, month + 1, 0);
    const endDay = String(endOfMonth.getDate()).padStart(2, "0");
    const endMonth = String(endOfMonth.getMonth() + 1).padStart(2, "0");

    const startDateFormatted = `${startDay}-${startMonth}-${year}`;
    const endDateFormatted = `${endDay}-${endMonth}-${year}`;

    return { startDateFormatted, endDateFormatted };
  };

  const { startDateFormatted, endDateFormatted } = getCurrentMonthDates();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { response, data: json } = await postDashboardDatatable(
        token,
        'get_inprogressInvoice',
        buildDatatablePayload({
          page,
          rowsPerPage,
          startDate: s,
          endDate: e,
          searchValue,
        })
      );

      if (!response?.ok) {
        throw new Error(`Failed to fetch data: ${response?.status ?? 'unknown'}`);
      }

      if (json && Array.isArray(json.data)) {
        setData(json.data);
        setTotalCount(json.recordsTotal || json.recordsFiltered || json.data.length);
      }
    } catch (error) {
      console.error('Error fetching inprogress invoice data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, s, e, searchValue]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = "/react/login";
      return;
    }
    fetchData();
  }, [fetchData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      setPage(0);
      fetchData();
    }
  };

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const formatRowDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  return (
    <>
      <Box className="page-container" >
        <Paper
          className="paper-style"
        >
          <Box sx={{ px:2, py:1.2}}>
            <Grid2 container spacing={2} display="flex" justifyContent="space-between" alignItems="center">
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography className="page-title">
                  Inprogress Invoice
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent='space-between'>
                <TextField
                  className="input-border"
                  size="small"
                  placeholder="Search..."
                  fullWidth
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill"/>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    },
                  }}
                />
                    <Typography sx={{ color: "#212121", fontSize: { xs: "0.75rem", sm: "0.875rem" }, whiteSpace: 'nowrap' }}>
                  From: {s ? formatDate(s) : startDateFormatted} &nbsp;
                  To: {e ? formatDate(e) : endDateFormatted}
                </Typography>
                </Stack>
              </Grid2>
            </Grid2>
          </Box>
          <TableContainer className="table-cell-border">
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            )}
            <Table
              size="medium"
              className="table-cell-border"
            >
              <TableHead>
                <TableRow className="table-cell-header">
                  <TableCell
                    align="center"
                    width="100px"
                  >
                    Order Date
                  </TableCell>
                  <TableCell
                    align="left"
                    width="200px"
                  >
                    Customers (A/C No)
                  </TableCell>
                  <TableCell
                    align="center"
                    width="100px"
                  >
                    Order Id
                  </TableCell>
                  <TableCell
                    align="right"
                    width="100px"
                  >
                    Order Amt
                  </TableCell>
                  <TableCell
                    align="center"
                    width="100px"
                  >
                    Route
                  </TableCell>
                  <TableCell
                    align="left"
                    width="100px"
                  >
                    Order By
                  </TableCell>
                  <TableCell
                    align="center"
                    width="100px"
                  >
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && data && data.length > 0 ? (
                  data.map((row, index) => (
                    <TableRow
                      key={index}
                      hover
                      className="table-cell-style"
                    >
                      <TableCell
                        align="center"
                      
                      >
                        {formatRowDate(row.date)}
                      </TableCell>
                      <TableCell
                        align="left"
                   
                      >
                        {row.customer}
                      </TableCell>
                      <TableCell
                        align="center"
                       
                      >
                        {row.reference_no}
                      </TableCell>
                      <TableCell
                        align="right"
                       
                      >
                        {row.grand_total}
                      </TableCell>
                      <TableCell
                        align="center"
          
                      >
                        {row.route_number}
                      </TableCell>
                      <TableCell
                        align="left"
                
                      >
                        {row.name}
                      </TableCell>
                      <TableCell
                        align="center"
                    
                      >
                        {row.sale_status}
                      </TableCell>
                    </TableRow>
                  ))
                ) : !loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{ py: 8 }}
                    >
                      <Typography align="center" sx={{ color: "#212121", fontSize: "0.875rem" }}>
                        No data found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
              className="pagination-styles"
            />
          )}
        </Paper>
      </Box>
    </>
  );
};

export default DeliveredInvoice;
